import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';

export const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:4000';

// The access JWT lives in memory only (spec 3.3) — never localStorage.
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Called when a refresh attempt fails — the auth context drops to guest. */
let onSessionExpired: (() => void) | null = null;
export function setOnSessionExpired(cb: (() => void) | null): void {
  onSessionExpired = cb;
}

export interface SessionResponse {
  access_token: string;
  user: {
    id: string;
    email: string;
    email_verified: boolean;
    display_name: string;
    leaderboard_name: string;
    can_set_leaderboard_name: boolean;
    email_opt_out: boolean;
    results_digest_opt_out: boolean;
  };
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // carries the httpOnly refresh cookie
  timeout: 15_000,
});

/** Seconds of headroom before `exp` at which a token counts as spent — covers
 *  clock skew and the request's own flight time. */
const EXPIRY_SKEW_S = 30;

/** `exp` from a JWT payload, or null if it isn't readable. Signature is the
 *  server's business; this only needs to know when to stop sending it. */
function tokenExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: number;
    };
    return typeof json.exp === 'number' ? json.exp : null;
  } catch {
    return null;
  }
}

function tokenIsSpent(token: string): boolean {
  const exp = tokenExpiry(token);
  // Unreadable token: assume it's fine and let the 401 path handle it, rather
  // than refreshing on every single request.
  if (exp === null) return false;
  return Date.now() / 1000 >= exp - EXPIRY_SKEW_S;
}

api.interceptors.request.use(async (config) => {
  // Refresh a spent token BEFORE sending, not after a 401.
  //
  // The response interceptor below only refreshes on a 401, which never
  // arrives from an `optionalAuth` route: those treat an expired token as
  // "no token" and answer 200 as though you were a guest. So a tab left open
  // past expiry would quietly lose its identity — the season strip fell back
  // to "Finish a board to start your season" for a player who had points, and
  // `/api/puzzles/today` computed GUEST BOARD LOCKS for someone signed in.
  //
  // Refreshing here fixes every optionalAuth route at once. `refreshSession`
  // dedupes concurrent callers, so a burst of queries triggers one refresh.
  // Auth routes are excluded, as they are in the 401 path: signing in while a
  // spent token is still in memory must not first renew the OLD session.
  const isAuthRoute = config.url?.includes('/api/auth/') ?? false;
  if (!isAuthRoute && accessToken && tokenIsSpent(accessToken)) {
    await refreshSession();
  }
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// One in-flight refresh shared across concurrent 401s.
let refreshInFlight: Promise<SessionResponse | null> | null = null;

export async function refreshSession(): Promise<SessionResponse | null> {
  refreshInFlight ??= axios
    .post<SessionResponse>(`${API_BASE_URL}/api/auth/refresh`, null, { withCredentials: true })
    .then((res) => {
      setAccessToken(res.data.access_token);
      return res.data;
    })
    .catch(() => {
      setAccessToken(null);
      onSessionExpired?.();
      return null;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

// 401 → refresh once → retry the original request (spec 11.4).
api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const isAuthRoute = original?.url?.includes('/api/auth/') ?? false;
    if (error.response?.status === 401 && original && !original._retried && !isAuthRoute) {
      original._retried = true;
      const session = await refreshSession();
      if (session) {
        original.headers.Authorization = `Bearer ${session.access_token}`;
        return api(original);
      }
    }
    throw error;
  },
);

/** Server error envelope { error_code, message, details? } → readable message. */
export function apiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
    if (err.code === 'ERR_NETWORK') return 'Network error — check your connection';
  }
  return fallback;
}

export function apiErrorCode(err: unknown): string | null {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error_code?: string } | undefined;
    return data?.error_code ?? null;
  }
  return null;
}
